import { ClockIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { randomString } from '../../../../../../../../functions/string.functions.ts';
import { MediaType } from '../../../../../../Chat.enum.ts';
import {
  parseSlides,
  scheduledTemplateOutcome,
} from '../../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import {
  eScheduledTemplateOutcome,
  tAttachment,
  tChatEvent,
} from '../../../../../../Chat.types.ts';
import useProgrammedMessageAction from '../../../../../MessageBar/components/ProgrammedMessageAction/ProgrammedMessageAction.hook.ts';
import { isDocumentOrPdf } from '../../../../../MessageBar/components/Thumbnail/Thumbnail.functions.ts';
import { findOutboundRefusal } from '../../../../../../../../utils/ErrorDirectory.ts';
import {
  META_ERROR_NOTICE_NAMESPACE,
  noticeStatusKey,
} from '../../../../../../../../utils/metaErrorNotice.ts';
import AudioContent from '../MessageBubble/components/AudioContent/AudioContent.component.tsx';
import FileContent from '../MessageBubble/components/FileContent/FileContent.component.tsx';
import ImageContent from '../MessageBubble/components/ImageContent/ImageContent.component.tsx';
import LocationContent from '../MessageBubble/components/LocationContent/LocationContent.component.tsx';
import StickerContent from '../MessageBubble/components/StickerContent/StickerContent.component.tsx';
import VideoContent from '../MessageBubble/components/VideoContent/VideoContent.component.tsx';
import CatalogMetaErrorNotice from '../MessageBubble/components/MetaErrorNotice/CatalogMetaErrorNotice.component.tsx';
import MetaErrorNotice from '../MessageBubble/components/MetaErrorNotice/MetaErrorNotice.component.tsx';
import { tMetaErrorNoticeInternalButton } from '../MessageBubble/components/MetaErrorNotice/MetaErrorNotice.types.ts';
import useBubbleDate from '../../../../bubbleDate/useBubbleDate.hook';
import ScheduledLegend from '../ScheduledLegend/ScheduledLegend.component.tsx';
import { useSchedulerName } from '../ScheduledLegend/ScheduledLegend.hook.ts';
import { resolveScheduledFailureNotice } from './FollowUpBubble.functions.ts';
import { useScheduledDateFormat } from './FollowUpBubble.hook.ts';
import '../MessageBubble/MessageBubbleV2.styles.scss';
import './FollowUpBubble.styles.scss';

function RenderFileType({ attachment }: { attachment: tAttachment }) {
  switch (attachment.mediaType) {
    case MediaType.IMAGE:
      return <ImageContent attachment={attachment} />;
    case MediaType.VIDEO:
      return <VideoContent attachment={attachment} />;
    case MediaType.AUDIO:
      return <AudioContent attachment={attachment} />;
    case MediaType.STICKER:
      return <StickerContent attachment={attachment} />;
    case MediaType.DOCUMENT:
      return <FileContent attachment={attachment} />;
    case MediaType.LOCATION:
      return (
        <LocationContent
          latitude={attachment.latitude ?? 0}
          longitude={attachment.longitude ?? 0}
        />
      );
    default:
      return <FileContent attachment={attachment} />;
  }
}

/**
 * A scheduled message or template in the chat thread (INT-1505).
 *
 * - Pending: a dashed card, "Programado para hoy 18:00", with Editar and
 *   Cancelar envío.
 * - Sent: the card gives way to the message it became (MessageContainer
 *   pairs them). While that message is not in the thread the card renders
 *   nothing: it never says "Enviado" with no message behind it (INT-1500 U8).
 * - Failed: nothing else will show this message, so the card takes its place
 *   as an outgoing bubble with the legend and the catalog notice underneath
 *   (INT-1500 U9), plus "Eliminar". A send Leadsales refused reads "No
 *   enviado" (U8, D7); one Meta refused reads "No entregado".
 */
export default function FollowUpBubble(followUpEvent: tChatEvent) {
  const bubbleDate = useBubbleDate();
  const { deleteFollowUp, loading } = useProgrammedMessageAction();
  const { selectedChat, setSelectedFollowUp } = useChatContext();
  const { t } = useTranslation([
    'chat/chat',
    'errors/errors',
    META_ERROR_NOTICE_NAMESPACE,
  ]);
  const formatScheduledDate = useScheduledDateFormat();
  const schedulerName = useSchedulerName(followUpEvent.createdBy);

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  const isWapiTemplate = followUpEvent.isWapiTemplate ?? false;
  const isConsumed = followUpEvent.followUp?.consumed ?? false;
  const triggerDate = followUpEvent.followUp?.triggerDate ?? '';

  // BUG-741: report the real campaign outcome instead of an unconditional
  // future-tense promise.
  const outcome = scheduledTemplateOutcome(followUpEvent);
  const isPending = outcome === eScheduledTemplateOutcome.PENDING && !isConsumed;

  // Edit stays template-gated: a generic text edit does not reach the WBS
  // campaign that sends a template (BUG-741). Cancelling does reach it.
  const canEdit = isPending && !isWapiTemplate;

  function onEdit() {
    setSelectedFollowUp({
      id: followUpEvent.id,
      trigger_date: new Date(triggerDate || Date.now()),
      consumed: isConsumed,
      // BUG-741: keep the template identity and campaign link on the edit
      // selection — see FollowUp.component.tsx.
      isWapiTemplate,
      campaignId: followUpEvent.followUp?.campaignId ?? null,
      content: {
        text: followUpEvent.content?.text || '',
        attachments: followUpEvent.content?.attachments || undefined,
      },
    });
  }

  const attachments = followUpEvent.content?.attachments ?? [];
  const attachmentList = attachments.length ? (
    <div className="form-control gap-2">
      {attachments.map((attachment) => (
        <div
          key={randomString()}
          onClick={() => {
            if (attachment?.mimeType && !isDocumentOrPdf(attachment?.mimeType)) {
              setOpenLightbox(true);
            }
          }}
        >
          <RenderFileType attachment={attachment} />
        </div>
      ))}
      {openLightbox && (
        <Lightbox
          plugins={[Zoom, Captions, Thumbnails, Download, Fullscreen, Video]}
          open={openLightbox}
          close={() => setOpenLightbox(false)}
          slides={parseSlides(attachments)}
        />
      )}
    </div>
  ) : null;

  if (outcome === eScheduledTemplateOutcome.FAILED) {
    // A follow-up that came due while the plan was paused is failed for good
    // with chat code 1016 (1017/1018 when it could not be tied to its
    // workspace; wba-10113 on a scheduled template), and its stored reason is
    // the server's English refusal. Say it in the customer's words instead.
    const refusal = findOutboundRefusal(followUpEvent.errors);
    const notice = resolveScheduledFailureNotice(
      followUpEvent,
      selectedChat?.integration,
    );
    // Nothing went out when Leadsales refused it (D7).
    const statusKey = refusal ? 'status.notSent' : noticeStatusKey(notice);
    const dismiss: tMetaErrorNoticeInternalButton = {
      type: 'internal',
      label: t('scheduledMessage.dismiss'),
      // Delete must survive a terminal FAILURE — see FollowUp.component.tsx.
      // The reconciler sets `consumed` on failure too, so this is the only way
      // to dismiss it.
      onClick: () => deleteFollowUp(followUpEvent.id),
      disabled: loading,
    };

    return (
      <div className="MessageBubbleV2" data-testid="scheduled-failed">
        <div className="MessageBubbleV2__Wrapper col-span-12 chat-end justify-end">
          <div className="flex items-end gap-1 flex-row-reverse">
            <div className="MessageBubbleV2__Container MessageBubbleV2__Container--outgoing MessageBubbleV2__Container--tail-end MessageBubbleV2__Container--with-notice">
              <div className="MessageBubbleV2__Header justify-start">
                <div className="text-xs min-w-36 MessageBubbleV2__accent">
                  {schedulerName}
                </div>
                <div className="MessageBubbleV2__Header__Date justify-end MessageBubbleV2__accent">
                  <span className="MessageBubbleV2__Header__NotDelivered">
                    {bubbleDate.stamp(triggerDate)} ·{' '}
                    {t(statusKey, { ns: META_ERROR_NOTICE_NAMESPACE })}
                  </span>
                </div>
              </div>
              <div className="MessageBubbleV2__Content">
                {attachmentList}
                <p className="break-words whitespace-pre-wrap">
                  {followUpEvent.content?.text}
                </p>
              </div>
              <ScheduledLegend followUp={followUpEvent} />
              {refusal ? (
                <MetaErrorNotice
                  variant="ours"
                  title={t('scheduledMessage.failedTitle')}
                  text={t(refusal.messageKey, { ns: 'errors/errors' })}
                  action={dismiss}
                />
              ) : (
                <CatalogMetaErrorNotice
                  notice={notice}
                  eventId={followUpEvent.id}
                  secondaryAction={dismiss}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // INT-1500 U8 (R7, R8): a processed card that is not failed went out, and
  // MessageContainer hides it behind its message. Unpaired, it has nothing
  // true to say: it used to read "Enviado" over a send the back-off skipped.
  if (!isPending) return null;

  return (
    <div className="FollowUpBubble" data-testid="scheduled-card">
      <div className="FollowUpBubble__Header">
        <ClockIcon className="FollowUpBubble__Header__Icon" aria-hidden="true" />
        <span>
          {t('scheduledMessage.scheduledFor', {
            date: formatScheduledDate(triggerDate),
          })}
        </span>
        {schedulerName && (
          <span className="FollowUpBubble__Header__By">{schedulerName}</span>
        )}
      </div>
      {followUpEvent.content?.text ? (
        <p className="FollowUpBubble__Text">{followUpEvent.content.text}</p>
      ) : null}
      {attachmentList}
      <div className="FollowUpBubble__Actions">
        {canEdit ? (
          <button
            type="button"
            className="ls-btn ls-btn-xs ls-btn-ghost"
            onClick={onEdit}
          >
            {t('scheduledMessage.edit')}
          </button>
        ) : null}
        <button
          type="button"
          className="ls-btn ls-btn-xs ls-btn-ghost"
          onClick={() => deleteFollowUp(followUpEvent.id)}
          disabled={loading}
          aria-busy={loading}
        >
          {t('scheduledMessage.cancelSend')}
        </button>
      </div>
    </div>
  );
}
